import React, { useRef } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { novo } from '@opendoor/bricks/theme';

import { buildResponsiveStyling, InputSizeProp, SizeToStyleMap } from './helpers/inputSizing';

type InputContainerElementProps = {
  error?: boolean;
  disabled?: boolean;
  sizeVariant?: InputSizeProp;
};

const SIZE_TO_SERIALIZED_STYLES: SizeToStyleMap = {
  '200': css`
    padding: 18px;
    font-size: ${novo.fontSizes['16px']};
    line-height: ${novo.lineHeights['130']};
    letter-spacing: ${novo.letterSpacings['-1.4']};
  `,
  '300': css`
    padding: 21px 18px;
    font-size: ${novo.fontSizes['18px']};
    line-height: ${novo.lineHeights['120']};
    letter-spacing: ${novo.letterSpacings['-2.4']};
  `,
  '400': css`
    padding: 24px 22px;
    font-size: ${novo.fontSizes['20px']};
    line-height: ${novo.lineHeights['120']};
    letter-spacing: ${novo.letterSpacings['-2.4']};
  `,
  '600': css`
    padding: 28px 22px;
    font-size: ${novo.fontSizes['22px']};
    line-height: ${novo.lineHeights['112']};
    letter-spacing: ${novo.letterSpacings['-2.4']};
  `,
};

export const InputContainerElement = styled.div<InputContainerElementProps>`
  /* borders and outlines */
  border-radius: ${novo.radii.roundedSmooth};
  border: 1px solid ${novo.colors.warmgrey300};

  ${({ disabled }) =>
    disabled
      ? css`
          border-color: ${novo.colors.warmgrey300};
          color: ${novo.colors.warmgrey700};
        `
      : css`
          &:hover {
            border-color: ${novo.colors.warmgrey800};
            outline: 1px solid ${novo.colors.warmgrey800};
          }
        `};

  &:focus-within {
    border-color: ${novo.colors.brandBlue600};
    outline: 2px solid ${novo.colors.brandBlue600};
  }

  ${({ error }) =>
    error
      ? css`
          border-color: ${novo.colors.error500};
          outline: 2px solid ${novo.colors.error500};
          color: ${novo.colors.error500};
        `
      : ''};

  background: ${novo.colors.white0};

  /* layout */
  display: flex;
  align-items: center;

  /* padding and typography */
  ${({ sizeVariant }) =>
    buildResponsiveStyling(SIZE_TO_SERIALIZED_STYLES, sizeVariant ?? ['200', '300', '400', '600'])}

  font-family: ${novo.fonts.sans};
  font-weight: ${novo.fontWeights.normal};
`;

export type InputContainerProps = {
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  error?: boolean;
  disabled?: boolean;
  sizeVariant?: InputSizeProp;
  onClick?: React.MouseEventHandler;
  children: React.ReactNode;
};
export const InputContainer: React.FC<InputContainerProps> = ({
  error,
  disabled,
  sizeVariant,
  children,
  prefix,
  suffix,
  onClick,
}) => (
  <InputContainerElement
    error={error}
    disabled={disabled}
    sizeVariant={sizeVariant}
    onClick={onClick}
  >
    {prefix ?? null}
    {children}
    {suffix ?? null}
  </InputContainerElement>
);

type InputElementProps = {
  error?: boolean;
};

export const InputElement = styled.input<InputElementProps>`
  font: inherit;
  line-height: inherit;
  letter-spacing: inherit;
  color: inherit;

  display: inline-block;
  width: 100%;
  padding: 0;

  outline: none;
  box-shadow: none;
  border: none;

  background: transparent;
`;

export type InputProps = {
  id: string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  sizeVariant?: InputSizeProp;
} & Parameters<typeof InputElement>[0];

const Input: React.FC<InputProps> = ({
  suffix,
  prefix,
  disabled,
  error,
  sizeVariant,
  ...inputProps
}) => {
  // setup a ref so that if the user clicks on the padding of InputContainer we
  // can focus them onto our Input
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <InputContainer
      suffix={suffix}
      prefix={prefix}
      disabled={disabled}
      error={error}
      sizeVariant={sizeVariant}
      onClick={(event) => event.target !== inputRef.current && inputRef.current?.focus()}
    >
      <InputElement {...inputProps} disabled={disabled} error={error} ref={inputRef} />
    </InputContainer>
  );
};

export default Input;
